/**
 * Secure Storage Utility
 * Provides encrypted storage for sensitive data with expiration and integrity checks
 */

class SecureStorage {
  constructor() {
    this.keyPrefix = 'qualia_secure_';
    this.saltSuffix = '_2024_salt';
    this.iterations = 100000;
  }

  /**
   * Generate a cryptographic key using PBKDF2
   */
  async generateKey(password, salt) {
    if (!window.crypto || !window.crypto.subtle) {
      throw new Error('Web Crypto API not supported');
    }

    const encoder = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveBits', 'deriveKey']
    );
    
    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(salt),
        iterations: this.iterations,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt data with AES-GCM
   */
  async encrypt(data, keyIdentifier = 'default') {
    try {
      if (!window.crypto || !window.crypto.subtle) {
        // Fallback to Base64 for older browsers
        console.warn('Using fallback Base64 encoding - not secure!');
        return this.fallbackEncode(data);
      }

      const encoder = new TextEncoder();
      const salt = this.keyPrefix + keyIdentifier + this.saltSuffix;
      const key = await this.generateKey('qualia-encryption-key-v2', salt);
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      
      // Add timestamp for integrity check
      const dataWithTimestamp = JSON.stringify({
        data: data,
        timestamp: Date.now(),
        version: '2.0'
      });
      
      const encrypted = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encoder.encode(dataWithTimestamp)
      );
      
      const encryptedArray = new Uint8Array(encrypted);
      const combined = new Uint8Array(iv.length + encryptedArray.length);
      combined.set(iv);
      combined.set(encryptedArray, iv.length);
      
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      console.error('Encryption error:', error);
      // Fallback to Base64
      return this.fallbackEncode(data);
    }
  }

  /**
   * Decrypt data with AES-GCM
   */
  async decrypt(encryptedData, keyIdentifier = 'default') {
    try {
      if (!window.crypto || !window.crypto.subtle) {
        // Fallback for older browsers
        return this.fallbackDecode(encryptedData);
      }

      const combined = new Uint8Array(
        atob(encryptedData).split('').map(char => char.charCodeAt(0))
      );
      
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);
      
      const salt = this.keyPrefix + keyIdentifier + this.saltSuffix;
      const key = await this.generateKey('qualia-encryption-key-v2', salt);
      
      const decrypted = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        encrypted
      );
      
      const decoder = new TextDecoder();
      const decryptedString = decoder.decode(decrypted);
      
      // Parse and validate the decrypted data
      const parsedData = JSON.parse(decryptedString);
      
      // Check version compatibility
      if (parsedData.version && parsedData.version !== '2.0') {
        console.warn('Data version mismatch, may need migration');
      }
      
      return parsedData.data;
    } catch (error) {
      console.error('Decryption error:', error);
      // Try fallback Base64 decryption
      try {
        return this.fallbackDecode(encryptedData);
      } catch (fallbackError) {
        console.error('Fallback decryption failed:', fallbackError);
        throw new Error('Unable to decrypt data');
      }
    }
  }

  /**
   * Fallback encoding for older browsers
   */
  fallbackEncode(data) {
    const dataWithTimestamp = JSON.stringify({
      data: data,
      timestamp: Date.now(),
      version: '1.0',
      fallback: true
    });
    return btoa(encodeURIComponent(dataWithTimestamp));
  }

  /**
   * Fallback decoding for older browsers
   */
  fallbackDecode(encodedData) {
    const decodedString = decodeURIComponent(atob(encodedData));
    const parsedData = JSON.parse(decodedString);
    return parsedData.data;
  }

  /**
   * Store encrypted data with expiration
   */
  async setItem(key, value, expirationDays = 30) {
    try {
      const encryptedValue = await this.encrypt(value, key);
      const expirationTime = Date.now() + (expirationDays * 24 * 60 * 60 * 1000);
      
      localStorage.setItem(this.keyPrefix + key, encryptedValue);
      localStorage.setItem(this.keyPrefix + key + '_expiry', expirationTime.toString());
      
      return true;
    } catch (error) {
      console.error('Error storing encrypted data:', error);
      return false;
    }
  }

  /**
   * Retrieve and decrypt data with expiration check
   */
  async getItem(key) {
    try {
      const encryptedValue = localStorage.getItem(this.keyPrefix + key);
      const expirationTime = localStorage.getItem(this.keyPrefix + key + '_expiry');
      
      if (!encryptedValue) {
        return null;
      }
      
      // Check expiration
      if (expirationTime && Date.now() > parseInt(expirationTime)) {
        this.removeItem(key);
        return null;
      }
      
      return await this.decrypt(encryptedValue, key);
    } catch (error) {
      console.error('Error retrieving encrypted data:', error);
      // Clean up corrupted data
      this.removeItem(key);
      return null;
    }
  }

  /**
   * Remove encrypted data and its expiration
   */
  removeItem(key) {
    localStorage.removeItem(this.keyPrefix + key);
    localStorage.removeItem(this.keyPrefix + key + '_expiry');
  }

  /**
   * Check if item exists and is not expired
   */
  hasItem(key) {
    const encryptedValue = localStorage.getItem(this.keyPrefix + key);
    const expirationTime = localStorage.getItem(this.keyPrefix + key + '_expiry');
    
    if (!encryptedValue) {
      return false;
    }
    
    if (expirationTime && Date.now() > parseInt(expirationTime)) {
      this.removeItem(key);
      return false;
    }
    
    return true;
  }

  /**
   * Clear all secure storage items
   */
  clear() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(this.keyPrefix)) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Get storage statistics
   */
  getStats() {
    const keys = Object.keys(localStorage);
    const secureKeys = keys.filter(key => key.startsWith(this.keyPrefix));
    
    return {
      totalItems: secureKeys.length / 2, // Each item has data + expiry
      storageKeys: secureKeys,
      cryptoSupported: !!(window.crypto && window.crypto.subtle)
    };
  }
}

// Export singleton instance
export default new SecureStorage();